import "./UserProfile.scss";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";

import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import chevronLeft from "../../assets/images/icons/chevron-left.svg";
import upload from "../../assets/images/icons/upload.svg";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import { getGeo, geoLabel } from "../../services/posthogGeo";
import CalculateAge from "../../utils/CalculateAge";
import ReactRating from "react-rating";
import { CiLocationOn, CiClock2 } from "react-icons/ci";
import { SlShare } from "react-icons/sl";
import { Link } from "react-router-dom";
import defaultPhoto from "../../assets/images/icons/profileDefault.png";
import Button from "../../components/Buttons/Button";
import { Tab, TabsContainer } from "../../utils/tab";

// Display names for the ZONA app's city pill (user.selected_city) -- the
// city the user picked to browse, NOT where they physically are. Mirrors
// the live /app-config city roster; new cities need a line added here.
const CITY_PILL_LABELS = {
  miami: "Miami", nyc: "New York", vegas: "Las Vegas",
};

// Mirrors ~/Zona/src/data/favoriteDrinks.js FAVORITE_DRINKS labels -- the
// id list is structural (needs a mobile build to change), keep in sync.
const DRINK_LABELS = {
  martini: "Martini", negroni: "Negroni", tom_collins: "Tom Collins",
  manhattan: "Manhattan", old_fashioned: "Old Fashioned", whiskey_sour: "Whiskey Sour",
  margarita: "Margarita", paloma: "Paloma", tequila_sunrise: "Tequila Sunrise",
  cosmopolitan: "Cosmopolitan", moscow_mule: "Moscow Mule", espresso_martini: "Espresso Martini",
  gin: "Gin", tequila: "Tequila", bourbon: "Bourbon", vodka: "Vodka",
  beer: "Beer", mocktail: "Mocktail",
};

// Event taxonomy + detail/duration formatting for the Activity tab below.
// This used to live in its own page (pages/Analytics/UserActivity.jsx,
// reached via a separate /analytics/user/:userId route) before that page
// was folded into this tab and deleted -- kept as local consts rather than
// a shared module since nothing else uses them now.
const ENGAGEMENT_EVENT_LABELS = {
  venue_view: 'Viewed venue', event_view: 'Viewed event', search: 'Searched',
  venue_tap: 'Tapped venue', event_tap: 'Tapped event',
  bottle_service_view: 'Viewed bottle service', bottle_purchase_started: 'Started bottle purchase',
  ai_query: 'Asked AI', map_opened: 'Opened map', venue_favorited: 'Favorited venue',
  view_tables_clicked: 'Viewed tables', join_guestlist_clicked: 'Clicked join guestlist',
  guestlist_join_blocked: 'Blocked from guestlist', guestlist_joined: 'Joined guestlist',
  verification_prompt_shown: 'Saw "Get verified" prompt', verification_prompt_tapped: 'Tapped "Get verified"',
  verification_prompt_dismissed: 'Dismissed verify prompt', verification_screen_viewed: 'Opened verification screen',
  verification_selfie_captured: 'Took verification selfie', verification_submit_attempted: 'Tried to submit verification',
  verification_blocked_profile_incomplete: 'Verify blocked — profile incomplete',
  verification_submit_failed: 'Verification submit failed', verification_submitted: 'Submitted verification',
  ad_impression: 'Saw ad', ad_click: 'Tapped ad', ad_dismiss: 'Dismissed ad',
  app_update_popup_shown: 'Saw app popup', app_update_popup_tapped: 'Tapped app popup',
  force_update_shown: 'Hit update screen', force_update_tapped: 'Tapped Update Now',
  city_switched: 'Switched city pill',
};
const ENGAGEMENT_REASON_LABELS = { verification_required: 'needs verification', profile_incomplete: 'profile incomplete' };

function UserProfile() {
  const { userID } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [userInfo, setUserInfo] = useState([]);
  const [userCheckIns, setUserCheckIns] = useState([]);
  const [pastEventsList, setPastEventsList] = useState([]);
  // null (not fetched yet) vs [] (fetched, empty) drives the lazy-load
  // guard below -- don't initialize this to [], it would look identical
  // to "already fetched, zero activity" and skip the query entirely.
  const [engagementFeed, setEngagementFeed] = useState(null);
  const [engagementLoading, setEngagementLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [cityLabel, setCityLabel] = useState("");
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // Where the user actually connected from (IP + geo-IP), from PostHog.
  const [geo, setGeo] = useState(null);

  const [galleryActive, setGalleryActive] = useState(searchParams.get('tab') !== 'activity')
  const [favoriteEventsActive, setFavoriteEventsActive] = useState(false);
  const [pastEventsActive, setPastEventsActive] = useState(false);
  const [engagementActive, setEngagementActive] = useState(searchParams.get('tab') === 'activity');
  const [followingActive, setFollowingActive] = useState(false);
  const [checkInActive, setCheckInActive] = useState(false);
  const headings = ["Check in", "Date", "Time", "Ratings", "Comments"];

  const birthday = CalculateAge(userInfo?.birthday);

  const handleRestore = async () => {
    if (restoring) return;
    if (!window.confirm("Restore this account? The user will be able to sign in again.")) return;
    setRestoring(true);
    try {
      // 15s timeout so a Firebase API stall doesn't leave the button
      // stuck disabled with no recourse short of a reload.
      await api.post(`/users/admin/restore/${userID}`, undefined, { timeout: 15000 });
      // Refetch BOTH the user record and the past-events list so the
      // page reflects the restored state without stale siblings.
      const [userResp, pastResp] = await Promise.all([
        api.get(`/users/${userID}`),
        api.get(`/events/users/${userID}/past`),
      ]);
      setUserInfo(userResp.data.user);
      setUserCheckIns(userResp.data.check_ins);
      setPastEventsList(pastResp.data);
    } catch (err) {
      const msg = err?.code === 'ECONNABORTED'
        ? 'Restore timed out. Try again.'
        : (err?.response?.data?.message || 'Restore failed. Try again.');
      window.alert(msg);
    } finally {
      setRestoring(false);
    }
  };

  useEffect(() => {
    const getUser = async () => {
      try {
        const response = await api.get(`/users/${userID}`);

        // setUserPhoto(`data:image/jpeg;base64,${response.data.photo}`);
        setUserCheckIns(response.data.check_ins);
        setUserInfo(response.data.user);
      } catch (err) {
        console.log(`error fetching user. this is the error: ${err}`);
      }
    };

    const getPastEvents = async () => {
      try {
        const response = await api.get(`/events/users/${userID}/past`);

        setPastEventsList(response.data);
      } catch (err) {
        console.log(
          `error fetching list past Events. this is the erro: ${err}`
        );
      }
    };

    getPastEvents();
    getUser();
  }, [userID]);

  // Lazy: only queries the 90-day engagement feed once the admin actually
  // opens the Activity tab, not on every profile load (most profile visits
  // -- checking a photo, restoring a soft-deleted account -- never touch
  // this tab, and it's a real backend query each time).
  useEffect(() => {
    if (!engagementActive || engagementFeed !== null) return;
    let cancelled = false;
    setEngagementLoading(true);
    api.get(`/analytics/user-activity/${userID}?days=90`)
      .then((response) => {
        if (!cancelled) setEngagementFeed(response.data.data || []);
      })
      .catch((err) => {
        console.log(`error fetching engagement feed. this is the error: ${err}`);
        if (!cancelled) setEngagementFeed([]);
      })
      .finally(() => {
        if (!cancelled) setEngagementLoading(false);
      });
    return () => { cancelled = true; };
  }, [engagementActive, engagementFeed, userID]);

  const cityPillLabel = (key) => (key ? (CITY_PILL_LABELS[key] || key) : 'unknown');

  const engagementDetail = (activity) => {
    if (activity.event_type === 'city_switched') {
      return `${cityPillLabel(activity.meta?.from_city)} → ${cityPillLabel(activity.meta?.to_city)}`;
    }
    if (activity.query) return `"${activity.query}"`;
    const parts = [];
    if (activity.reason) parts.push(ENGAGEMENT_REASON_LABELS[activity.reason] || activity.reason);
    if (activity.venue_name) parts.push(activity.venue_name);
    if (activity.event_name) parts.push(activity.event_name);
    return parts.join(' · ');
  };

  const engagementDuration = (ms) => {
    if (!ms || ms < 1000) return '';
    const s = Math.round(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.round(s / 60)}m`;
  };

  const formatEngagementTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  // Profiles store location as raw "lat, lng" coordinates. Resolve them to a
  // readable city via a free, no-key reverse-geocode service. If the stored
  // value is already a place name (not coordinates), show it as-is.
  useEffect(() => {
    // Respect the user's location-sharing setting: if they're not sharing,
    // don't geocode — the profile shows "location hidden" instead.
    if (userInfo?.location_sharing === false) {
      setCityLabel("");
      return;
    }
    const loc = userInfo?.location;
    if (!loc) {
      setCityLabel("");
      return;
    }
    const coords = /^\s*(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*$/.exec(loc);
    if (!coords) {
      setCityLabel(loc);
      return;
    }
    const [, lat, lng] = coords;
    let cancelled = false;
    setCityLabel("Locating…");
    fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`
    )
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const city = d.city || d.locality || d.principalSubdivision;
        const region = d.principalSubdivision || d.countryName;
        setCityLabel(
          city ? (region && region !== city ? `${city}, ${region}` : city) : loc
        );
      })
      .catch(() => {
        if (!cancelled) setCityLabel(loc);
      });
    return () => {
      cancelled = true;
    };
  }, [userInfo?.location, userInfo?.location_sharing]);

  // Where they actually connected from (IP + geo-IP), keyed on firebase uid.
  useEffect(() => {
    const uid = userInfo?.id;
    if (!uid) return;
    let cancelled = false;
    getGeo(uid).then((res) => {
      if (!cancelled) setGeo(res?.geo || null);
    });
    return () => {
      cancelled = true;
    };
  }, [userInfo?.id]);

  function handleSubNavClick(tab) {
    setGalleryActive(tab === "gallery");
    setFavoriteEventsActive(tab === "favEvents");
    setPastEventsActive(tab === "pastEvents");
    setEngagementActive(tab === "activity");
    setFollowingActive(tab === "following");
    setCheckInActive(tab === "checkins");
    // Keep ?tab= in sync so the Activity deep link (from the Analytics page
    // and EventsDetail) doesn't reopen on the wrong tab after a refresh or
    // back/forward once the admin has manually switched tabs. replace, not
    // push, so tab-clicking doesn't spam browser history.
    setSearchParams(tab === "activity" ? { tab: "activity" } : {}, { replace: true });
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { month: "short", day: "2-digit", year: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const options = { hour: "2-digit", minute: "2-digit", hour12: true };
    return date.toLocaleTimeString("en-US", options);
  };

  const renderRatingStars = (rating) => {
    return (
      <ReactRating
        readonly
        initialRating={rating}
        emptySymbol={
          <span style={{ color: "rgb(167, 41, 245)", fontSize: "24px" }}>
            &#9734;
          </span>
        }
        fullSymbol={
          <span style={{ color: "rgb(167, 41, 245)", fontSize: "24px" }}>
            &#9733;
          </span>
        }
        fractions={2}
      />
    );
  };

  const formatHours = (hours) => {
    const formatTime = (totalMinutes) => {
      // wrap times beyond 24h (e.g., 1740 → 300 → 5:00 AM)
      let h = Math.floor((totalMinutes % 1440) / 60);
      const m = String(totalMinutes % 60).padStart(2, "0");
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12 || 12; // convert 0 → 12
      return `${h}:${m} ${ampm}`;
    };

    return `${formatTime(hours.from)} - ${formatTime(hours.to)}`;
  };

  const getValidImage = (img) => {
    if (!img) return null;

    // If it contains multiple URLs, split and take the last valid one
    if (img.includes("https://") && img.indexOf("https://") !== img.lastIndexOf("https://")) {
      return img.slice(img.lastIndexOf("https://"));
    }

    return img;
  };




  return (
    <main>
      <TopNav />

      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="user-profile">
            <div className="user-profile__top flex-wrap gap-1">
              <div className="user-profile__top-left">
                <img
                  src={chevronLeft}
                  onClick={() => navigate(-1)}
                  className="user-profile__top-left-icon"
                  alt="arrow head poining left"
                ></img>
                <h1 className="user-profile__top-left-heading">User Profile</h1>
              </div>

              <Button
                isLink
                to={`/users/edit/${userID}`}
                label="Edit User Details"
                className="btn-outline-light"
              />
            </div>

            {userInfo?.deleted_at && (
              <div
                style={{
                  background: "rgba(239, 68, 68, 0.12)",
                  border: "1px solid rgba(239, 68, 68, 0.4)",
                  borderRadius: 10,
                  padding: "12px 16px",
                  margin: "12px 0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ color: "#fff", fontSize: 13 }}>
                  <strong style={{ color: "#EF4444" }}>Account soft-deleted.</strong>{" "}
                  Deleted {new Date(userInfo.deleted_at).toLocaleString("en-US", { timeZone: "America/New_York" })}.
                  {" "}This row will be permanently removed by the 3am Miami reaper 30 days after that timestamp.
                </div>
                <button
                  onClick={handleRestore}
                  disabled={restoring}
                  style={{
                    background: restoring ? "#555" : "#0A3FD6",
                    color: "#fff",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: restoring ? "not-allowed" : "pointer",
                    opacity: restoring ? 0.7 : 1,
                  }}
                >
                  {restoring ? "Restoring…" : "Restore account"}
                </button>
              </div>
            )}

            <section className="user-profile__user align-items-start gap-3 pt-4 pb-4">
              {userInfo.photo_url ? (
                <img
                  className="user-profile__user-avatar"
                  src={userInfo.photo_url}
                  alt={`${userInfo.firstName} ${userInfo.lastName} avatar`}
                  title="View full size"
                  onClick={() => setLightboxUrl(userInfo.photo_url)}
                  style={{ width: 120, height: 120, minWidth: 120, borderRadius: 60, objectFit: 'cover', cursor: 'pointer', display: 'block' }}
                />
              ) : (
                <img
                  className="user-profile__user-avatar"
                  src={defaultPhoto}
                  alt="avatar"
                  style={{ width: 120, height: 120, minWidth: 120, borderRadius: 60, objectFit: 'cover', display: 'block' }}
                />
              )}

              <div className="user-profile__user-writing">
                <h2 className="user-profile__user-writing-heading">
                  {userInfo?.firstName} {userInfo?.lastName}
                </h2>
                <p className="user-profile__user-writing-text" style={{paddingBottom: 4, paddingTop: 8}}>
                  {birthday}-y.o, {userInfo?.gender || 'gender not known'}{userInfo?.location_sharing === false ? ', location hidden' : (cityLabel ? `, Living in ${cityLabel}` : '')}
                </p>
                {geoLabel(geo) && (
                  <p className="user-profile__user-writing-text" style={{paddingBottom: 4}}>
                    <span style={{ color: 'rgba(255,255,255,0.55)' }}>Connected from </span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{geoLabel(geo)}</span>
                    {geo?.ip && (
                      <span style={{ display: 'block', color: 'rgba(255,255,255,0.35)', fontSize: 11, fontFamily: 'monospace', marginTop: 2, wordBreak: 'break-all' }}>
                        {geo.ip}
                      </span>
                    )}
                  </p>
                )}
                {userInfo?.selected_city && (
                  <p className="user-profile__user-writing-text" style={{paddingBottom: 4}}>
                    <span style={{ color: 'rgba(255,255,255,0.55)' }}>ZONA city pill: </span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>
                      {CITY_PILL_LABELS[userInfo.selected_city] || userInfo.selected_city}
                    </span>
                  </p>
                )}
                <p className="user-profile__user-writing-text" style={{paddingBottom: 4}}>
                  {userInfo?.ig && `IG: @${userInfo.ig}`}
                </p>
                {userInfo?.favorite_drink?.id && (
                  <p className="user-profile__user-writing-text" style={{paddingBottom: 4}}>
                    <span style={{ color: 'rgba(255,255,255,0.55)' }}>Favorite drink: </span>
                    <span style={{ color: '#fff', fontWeight: 600 }}>
                      {DRINK_LABELS[userInfo.favorite_drink.id] || userInfo.favorite_drink.id}
                      {userInfo.favorite_drink.brand ? ` (${userInfo.favorite_drink.brand})` : ''}
                    </span>
                  </p>
                )}
                {
                  userInfo?.bio && (
                    <p className="user-profile__user-writing-text">
                      <div style={{fontWeight: 'bold', marginBottom: 4, marginTop: 12}}>Bio:</div>
                      {userInfo?.bio}
                    </p>
                  )
                }
              </div>
            </section>


            <TabsContainer>
                <Tab
                    active={galleryActive}
                    onClick={() => handleSubNavClick("gallery")}
                >
                  Gallery ({userInfo?.gallery?.length || 0})
                </Tab>
                <Tab
                    active={pastEventsActive}
                    onClick={() => handleSubNavClick("pastEvents")}
                >
                  Past Events ({pastEventsList?.length || 0})
                </Tab>
                <Tab
                    active={engagementActive}
                    onClick={() => handleSubNavClick("activity")}
                >
                  Activity ({engagementFeed?.length || 0})
                </Tab>
                <Tab
                    active={followingActive}
                    onClick={() => handleSubNavClick("following")}
                >
                  Favorites ({userInfo?.favorites?.length || 0})
                </Tab>
            </TabsContainer>


            {
              galleryActive && (
                <div className="container_content_user">
                  <div className="row w-100 g-3">
                    {userInfo?.gallery?.map((i, idx) => (
                      <div 
                        key={idx}
                        className="col-lg-3 col-sm-6 col-12"
                      >
                          <img
                            src={i?.thumb_uri || i?.uri || i}
                            className="w-100"
                            style={{aspectRatio: 1, objectFit: 'cover', borderRadius: '0.5rem'}}
                          />
                      </div>
                    ))}
                  </div>
                </div>
              )
            }

            {favoriteEventsActive && (
              <div className="container_content_user">
                {userInfo?.saved?.map((item, index) => (
                    <div className="card_favorites" key={index}>
                      <div
                          style={{
                            backgroundImage: `url(${Array.isArray(item.image) ? getValidImage(item.image[0]) : getValidImage(item.image)})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                      ></div>


                      <div>
                        <h1>{item.name}</h1>
                      </div>

                      <div>
                        <p>
                          <CiLocationOn/>
                          {item.venue_name}
                        </p>

                        <p>
                          <CiClock2/>
                          <p> {formatHours(item.hours)}</p>
                        </p>
                      </div>
                    </div>
                ))}
              </div>
            )}

            {pastEventsActive && (
                <div className="container_content_user">
                  {pastEventsList.map((item, index) => (
                      <div className="card_favorites-events" key={index}>
                        <div
                            style={{
                        backgroundImage: `url(${
                          Array.isArray(item.image_urls)
                            ? item.image_urls[0]
                            : item.image_urls
                        })`,
                      }}
                    ></div>
                    <div>
                      <h1>{item.name}</h1>
                      <p>
                        <CiLocationOn
                          style={{ color: "rgba(255, 255, 255, 0.7)" }}
                        />
                        {item.address}
                      </p>
                      {item.activity && (item.activity.guestlist || item.activity.ticket || item.activity.dinner || item.activity.bottle) && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, margin: "6px 0" }}>
                          {item.activity.guestlist && (
                            <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                              background: item.activity.guestlist === "Accepted" ? "rgba(198,255,60,0.15)" : item.activity.guestlist === "Rejected" ? "rgba(225,29,72,0.15)" : "rgba(255,255,255,0.1)",
                              color: item.activity.guestlist === "Accepted" ? "#C6FF3C" : item.activity.guestlist === "Rejected" ? "#FF4D4F" : "rgba(255,255,255,0.75)" }}>
                              Guestlist · {item.activity.guestlist}
                            </span>
                          )}
                          {item.activity.ticket && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(79,48,234,0.25)", color: "#fff" }}>Ticket</span>}
                          {item.activity.dinner && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(230,126,34,0.2)", color: "#E67E22" }}>Dinner</span>}
                          {item.activity.bottle && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(255,255,255,0.12)", color: "#fff" }}>Bottle</span>}
                        </div>
                      )}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <p>
                          <CiClock2
                            style={{ color: "rgba(255, 255, 255, 0.7)" }}
                          />
                          {formatHours(item.hours)}
                        </p>
                        <Link to={item.url}>
                          <SlShare size={24} color="#fff" />
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {engagementActive && (
              <div className="container_content_user">
                <div style={{ width: '100%' }}>
                  {engagementLoading ? (
                    <div style={{ color: 'rgba(255,255,255,0.4)', padding: '24px 0' }}>Loading...</div>
                  ) : (
                    <table className="moderators__table" style={{ width: '100%' }}>
                      <thead className="moderators__table-top">
                        <tr className="moderators__table-top-headings">
                          <th className="moderators__table-top-headings-heading" scope="col">Time</th>
                          <th className="moderators__table-top-headings-heading" scope="col">Action</th>
                          <th className="moderators__table-top-headings-heading" scope="col">Detail</th>
                          <th className="moderators__table-top-headings-heading" scope="col">Source</th>
                        </tr>
                      </thead>
                      <tbody className="moderators__table-info">
                        {(engagementFeed || []).map((a, i) => (
                          <tr className="moderators__table-info-info" key={i}>
                            <td className="moderators__table-info-info-text" style={{ whiteSpace: 'nowrap' }}>{formatEngagementTime(a.created_at)}</td>
                            <td className="moderators__table-info-info-text">{ENGAGEMENT_EVENT_LABELS[a.event_type] || a.event_type}</td>
                            <td className="moderators__table-info-info-text">
                              {engagementDetail(a) || '—'}
                              {engagementDuration(a.duration_ms) && (
                                <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: 8 }}>
                                  ({engagementDuration(a.duration_ms)})
                                </span>
                              )}
                            </td>
                            <td className="moderators__table-info-info-text">{a.source || '—'}</td>
                          </tr>
                        ))}
                        {(engagementFeed || []).length === 0 && (
                          <tr><td colSpan={4} style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)' }}>No activity found</td></tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {followingActive && (
              <div className="container_content_user">
                <div className="content_following">
                  {userInfo?.favorites?.map((item) => (
                    <div key={item.id} className="card_following" style={{ width: '100%' }}>
                      <img src={item.image[0]} alt="Imagem" />

                      <h1>{item.name}</h1>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {checkInActive && (
              <div className="container_content_user">
                <div style={{ width: '100%' }}>
                  <table className="moderators__table" style={{ width: '100%' }}>
                    <thead className="moderators__table-top">
                      <tr className="moderators__table-top-headings">
                        {headings.map((heading, index) => (
                          <th
                            className="moderators__table-top-headings-heading"
                            key={index}
                            scope="col"
                          >
                            {heading}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody className="moderators__table-info">
                      {userCheckIns?.map((item) => (
                        <tr
                          className="moderators__table-info-info"
                          key={item._id}
                          style={{ width: '100%' }}
                        >
                          <td className="moderators__table-info-info-text">
                            {item?.venue_name}
                          </td>
                          <td className="moderators__table-info-info-text">
                            {formatDate(item?.created_at)}
                          </td>
                          <td className="moderators__table-info-info-text">
                            {formatTime(item?.created_at)}
                          </td>
                          <td className="moderators__table-info-info-text">
                            {renderRatingStars(item?.review?.rating)}
                          </td>
                          <td className="moderators__table-info-info-text moderators__table-info-info-text--status-box">
                            <p>{item?.review?.comments}</p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        </article>
      </div>

      {lightboxUrl && (
        <div
          onClick={() => setLightboxUrl(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, cursor: 'zoom-out' }}
        >
          <img src={lightboxUrl} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }} />
        </div>
      )}
    </main>
  );
}

export default UserProfile;
